import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Option = { label: string; value: string };

type Props = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  icon?: React.ReactNode;
  options: Option[];
  className?: string;
  disabled?: boolean;
};

export default function MultiSelectField({
  value,
  onChange,
  placeholder,
  icon,
  options,
  className = "",
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number; maxHeight: number }>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 256,
  });

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedLabels = useMemo(
    () => options.filter((option) => selectedSet.has(option.value)).map((option) => option.label),
    [options, selectedSet],
  );

  const displayValue = selectedLabels.length ? selectedLabels.join(", ") : placeholder;

  useEffect(() => {
    if (!open) return;
    const updateMenuPosition = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportMargin = 12;
      const gap = 8;
      const desiredHeight = 256;
      const availableBelow = window.innerHeight - rect.bottom - viewportMargin;
      const availableAbove = rect.top - viewportMargin;
      const shouldOpenAbove = availableBelow < desiredHeight && availableAbove > availableBelow;
      const maxHeight = Math.max(
        120,
        Math.min(desiredHeight, shouldOpenAbove ? availableAbove - gap : availableBelow - gap),
      );
      setMenuStyle({
        top: shouldOpenAbove ? Math.max(viewportMargin, rect.top - gap - maxHeight) : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
    };

    updateMenuPosition();

    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onWindowChange = () => updateMenuPosition();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open]);

  const toggleValue = (nextValue: string) => {
    if (selectedSet.has(nextValue)) {
      onChange(value.filter((item) => item !== nextValue));
      return;
    }
    onChange([...value, nextValue]);
  };

  return (
    <div
      ref={wrapRef}
      className={[
        "relative w-full rounded-2xl border border-slate-200 bg-white shadow-sm",
        open ? "z-30" : "z-10",
        "focus-within:border-slate-300 focus-within:shadow-md",
        className,
      ].join(" ")}
    >
      {icon && <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{icon}</div>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={[
          "w-full rounded-2xl bg-transparent py-[14px] pr-8 text-left text-[15px] outline-none",
          icon ? "pl-10" : "pl-4",
          disabled ? "cursor-not-allowed text-slate-400" : "text-slate-800",
        ].join(" ")}
      >
        <span className={["block truncate", selectedLabels.length ? "text-slate-800" : "text-slate-500"].join(" ")}>
          {displayValue}
        </span>
      </button>
      <svg
        className={["pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-transform", open ? "rotate-180" : ""].join(" ")}
        width="18"
        height="18"
        viewBox="0 0 24 24"
      >
        <path fill="currentColor" d="M7 10l5 5l5-5z" />
      </svg>

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[400] max-h-64 overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
            style={{
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
              maxHeight: menuStyle.maxHeight,
            }}
          >
            {options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(option.value)}
                  onChange={() => toggleValue(option.value)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-200"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
